import { useEffect, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";
import { ReflexContainer, ReflexElement, ReflexSplitter } from "react-reflex";

import AccessControl from "../functional/AccessControl";

import BasicCrudScreenActionItemLayout from "../screenTypes/layout/BasicCrudScreenActionItemLayout";
import BasicCrudScreenType from "../screenTypes/functional/BasicCrudScreenType";

import notificationState from "../../states/notificationState";
import authProfileState from "../../states/authProfileState";

import getSchema from "../../lib/api/getSchema";

import UpdateRecordButton from "../input/UpdateRecordButton";
import DeleteRecordButton from "../input/DeleteRecordButton";
import SaveToExcelButton from "../input/SaveToExcelButton";
import EguLabDataSheetCreateButton from "../input/EguLabDataSheetCreateButton";
import ApprovalButton from "../input/ApprovalButton";

import EguLabDataSheetDetail from "./EguLabDataSheetDetail";
import layoutConf from "../../config/layoutConf";

function EguLabDataSheet(props) {

    const { onSelectMst } = props;

    const [tableSchema, setTableSchema] = useState();
    const [grid, setGrid] = useState();
    const [api, setApi] = useState();
    const [, setApiDetail] = useState();
    const [isEmpty, setIsEmpty] = useState();
    const [selectMst, setSelectMst] = useState({});

    const [approvalCtx, setApprovalCtx] = useState();

    const [, setNotification] = useRecoilState(notificationState);

    const { t } = useTranslation();

    const authProfile = useRecoilValue(authProfileState);

    useEffect(() => {

        let approvalCtx = {
            approvalTypeId: 20,
            tableName: "EguLabDataSheet",
            statusColumn: "approvalStateId",
            schema: tableSchema,
            onValidateApprovalItem: (item) => {
                if (item.createdBy !== authProfile.username) {
                    return [false, t("error.approvalRequesterNotMatch")];
                }

                return [true, ""];
            },
            makeRequestItemDescription: (item) => {

                return `${t("approvalRequestMsg.msg.eguLabDataSheet", {
                    eguLabDataSheetId: item.eguLabDataSheetId,
                })}`; // Use Translation on Production Code
            },
        };

        setApprovalCtx(approvalCtx);

    }, [tableSchema, authProfile, t]);

    useEffect(() => {

        (async () => {

            if (grid) {

                grid.selectionChanged.addHandler(debounce((grid, event) => {

                    if (grid.selectedItems && grid.selectedItems[0]) {

                        setSelectMst(grid.selectedItems[0]);

                    }
                }, 10));

                grid.itemsSourceChanged.addHandler(debounce((grid, event) => {

                    if (grid.selectedItems && grid.selectedItems[0]) {

                        setSelectMst(grid.selectedItems[0]);
                    }
                }, 10));
            }
        })();

    }, [grid]);

    useEffect(() => {

        if (api) {

            api.refresh();

        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onSelectMst?.longLabNo]);

    useEffect(() => {

        let mounted = true;

        (async () => {

            let schema = await getSchema("EguLabDataSheet");

            if (mounted) {
                setTableSchema(schema);
            }

        })();

        return (() => {
            mounted = false;
        });

    }, []);


    const onLoadData = (data) => {

        setIsEmpty(data.length < 1 ? true : false)
        
        if (data.length < 1) {
            setSelectMst(null)
        }

    };

    const onInitialized = (grid) => {

        setGrid(grid);

    };

    const onApiInitialized = (api) => {

        setApi(api);

    };

    const notifySuccess = (msg) => {
        setNotification({
            severity: "success",
            msg: msg,
            open: true,
        });

        if (api) {

            api.refresh();

        }
    };

    const notifyFailure = (msg) => {
        setNotification({
            severity: "error",
            msg: msg,
            open: true,
        });
    };

    const notifyWarning = (msg) => {
        setNotification({
            severity: "warning",
            msg: msg,
            open: true,
        });
    };

    const onNotifyWarning = (msg) => {
        notifyWarning(msg);
    };

    const onFailure = (reason) => {
        notifyFailure(reason.response ? reason.response.data : reason);
    };

    const onSuccess = (msg) => {
        notifySuccess(msg);
    };

    const doHandleBackendErrorMsg = (reason) => {
        // reason.response := { data, status, statusText, headers, config, request }
        // data : user error message
        // status : http status code
        // statusText : http status code text
        const response = reason.response;
        return `${response.data} `;

    }

    const onAppendButtonInFrontOfSearch = () => {
        return (
            <>
                <BasicCrudScreenActionItemLayout>
                    <AccessControl
                        accessTarget={tableSchema && tableSchema.name}
                        accessMethod="POST"
                    >
                        <EguLabDataSheetCreateButton
                            id="EguLabDataSheet"
                            schema={tableSchema}
                            disabled={!onSelectMst?.longLabNo}
                            onSelectMst={onSelectMst}
                            color="primary"
                            createButtonName="create"
                            onSuccess={onSuccess}
                            refresh={api}
                        />
                    </AccessControl>

                    <AccessControl
                        accessTarget={tableSchema && tableSchema.name}
                        accessMethod={
                            tableSchema && (tableSchema.updateWithPatch ? "PATCH" : "PUT")
                        }
                    >
                        <UpdateRecordButton
                            id="EguLabDataSheet"
                            schema={tableSchema}
                            grid={grid}
                            refresh={api}
                            disabled={selectMst?.approvalState ? true : false || isEmpty}
                            onSuccess={onSuccess}
                        />
                    </AccessControl>

                    <AccessControl
                        accessTarget={tableSchema && tableSchema.name}
                        accessMethod="DELETE"
                    >
                        <DeleteRecordButton
                            id="EguLabDataSheet"
                            schema={tableSchema}
                            grid={grid}
                            refresh={api}
                            color="secondary"
                            disabled={selectMst?.approvalState ? true : false || isEmpty}
                            onSuccess={onSuccess}
                            onFailure={onFailure}
                            onPartialFailure={onNotifyWarning}
                        />
                    </AccessControl>
                    <AccessControl
                        accessTarget={tableSchema && tableSchema.name}
                        accessMethod="APPROVAL"
                    >
                        <ApprovalButton
                            id="EguLabDataSheet"
                            grid={grid}
                            refresh={api}
                            approvalCtx={approvalCtx}
                            disabled={isEmpty}
                            onSuccess={onSuccess}
                            onFailure={onFailure}
                            customizeErrorMsgCallback={doHandleBackendErrorMsg}
                        />
                    </AccessControl>
                    <AccessControl
                        accessTarget={tableSchema && tableSchema.name}
                        accessMethod="SAVETOEXCEL"
                    >
                        <SaveToExcelButton
                            id="EguLabDataSheet"
                            grid={grid}
                            view="EguVwLabDataSheet"
                            color="primary"
                            disabled={isEmpty}
                        />
                    </AccessControl>
                </BasicCrudScreenActionItemLayout>
            </>
        );
    };

    return (
        <>
            <ReflexContainer orientation="vertical">
                <ReflexElement>
                    <BasicCrudScreenType
                        id="EguLabDataSheet"
                        table="EguLabDataSheet"
                        view="EguVwLabDataSheet"
                        headerTitle={t("term.experiment")}
                        noCreate
                        noEdit
                        noDelete
                        noExcel
                        onLoadData={onLoadData}
                        onInitialized={onInitialized}
                        onApiInitialized={onApiInitialized}
                        onAppendButtonInFrontOfSearch={onAppendButtonInFrontOfSearch}
                        onModifyViewUrl={(url) => url + '/' + onSelectMst?.longLabNo}
                        exportFormatItem={() => { }}
                    />
                </ReflexElement>

                <ReflexSplitter style={{ width: layoutConf.reflex.splitterWidth }} />

                <ReflexElement>
                    <EguLabDataSheetDetail
                        selectMst={selectMst}
                        setApiDetail={setApiDetail}
                    />
                </ReflexElement>
            </ReflexContainer>
        </>

    );
}

export default EguLabDataSheet;
